/**
 * Apply admin preview query params before APP_SLUG is computed.
 * Must be imported first from index.tsx (side-effect).
 *
 * Preview flags + edition slug for admin live in sessionStorage (per tab / iframe).
 * Never write debug:slug:override from admin preview — that localStorage key is shared
 * across tabs and was poisoning the normal SPA after an experimental session.
 */

export const SLUG_OVERRIDE_KEY = 'debug:slug:override'
export const ADMIN_PREVIEW_SURVEY_KEY = 'adminPreview:surveyKey'
export const ADMIN_PREVIEW_ACTIVE_KEY = 'adminPreview:active'
export const ADMIN_PREVIEW_USERNAME_KEY = 'adminPreview:username'
export const ADMIN_PREVIEW_EMBED_KEY = 'adminPreview:embed'
export const ADMIN_PREVIEW_PAGE_KEY = 'adminPreview:page'
export const ADMIN_PREVIEW_EDITION_KEY = 'adminPreview:edition'
export const DEV_INSPECTOR_STORAGE_KEY = 'debug:devInspector'

function stripParams(params: URLSearchParams, keys: string[]): void {
  for (const key of keys) params.delete(key)
}

/** End admin preview / embed session for this browsing context. */
export function clearAdminPreviewSession(): void {
  try {
    sessionStorage.removeItem(ADMIN_PREVIEW_ACTIVE_KEY)
    sessionStorage.removeItem(ADMIN_PREVIEW_EMBED_KEY)
    sessionStorage.removeItem(ADMIN_PREVIEW_PAGE_KEY)
    sessionStorage.removeItem(ADMIN_PREVIEW_SURVEY_KEY)
    sessionStorage.removeItem(ADMIN_PREVIEW_USERNAME_KEY)
    sessionStorage.removeItem(ADMIN_PREVIEW_EDITION_KEY)
  } catch {
    // ignore
  }
}

/** Sync: read ?edition=&survey=&adminPreview=1&embed=admin&page=, persist, clean URL. */
export function applyAdminPreviewQueryFromLocation(): void {
  if (typeof window === 'undefined') return

  const params = new URLSearchParams(window.location.search)
  const edition = params.get('edition')?.trim() || ''
  const survey = params.get('survey')?.trim() || ''
  const adminPreview = params.get('adminPreview')
  const adminUsername = params.get('adminUsername')?.trim() || ''
  const embed = params.get('embed')?.trim() || ''
  const page = params.get('page')?.trim() || ''
  const touched = Boolean(
    edition || survey || adminPreview === '1' || adminUsername || embed === 'admin' || page,
  )

  // Normal SPA load (no preview params): drop sticky session left in this tab.
  // Playground iframes always reload with ?adminPreview=1&embed=admin in their src.
  if (!touched) {
    clearAdminPreviewSession()
    // One-shot: older admin preview wrote edition into shared localStorage and broke
    // the normal SPA. Clear that override once; Editions page can set it again.
    try {
      const migrationKey = 'adminPreview:slugFix20260710'
      if (localStorage.getItem(migrationKey) !== '1') {
        localStorage.removeItem(SLUG_OVERRIDE_KEY)
        localStorage.setItem(migrationKey, '1')
      }
    } catch {
      // ignore
    }
    return
  }

  const activatingPreview = adminPreview === '1' || embed === 'admin'

  if (edition) {
    if (activatingPreview) {
      // Per-tab only — do not touch localStorage debug:slug:override
      sessionStorage.setItem(ADMIN_PREVIEW_EDITION_KEY, edition)
    } else {
      // Legacy ?edition= without adminPreview (dev deep-link)
      localStorage.setItem(SLUG_OVERRIDE_KEY, edition)
    }
  }

  if (survey) {
    sessionStorage.setItem(ADMIN_PREVIEW_SURVEY_KEY, survey)
  } else if (edition) {
    sessionStorage.removeItem(ADMIN_PREVIEW_SURVEY_KEY)
  }

  if (activatingPreview) {
    if (embed !== 'admin') {
      localStorage.setItem(DEV_INSPECTOR_STORAGE_KEY, '1')
    }
    sessionStorage.setItem(ADMIN_PREVIEW_ACTIVE_KEY, '1')
  }

  if (embed === 'admin') {
    sessionStorage.setItem(ADMIN_PREVIEW_EMBED_KEY, '1')
  }

  if (page) {
    sessionStorage.setItem(ADMIN_PREVIEW_PAGE_KEY, page)
  } else if (activatingPreview) {
    sessionStorage.removeItem(ADMIN_PREVIEW_PAGE_KEY)
  }

  if (adminUsername) {
    sessionStorage.setItem(ADMIN_PREVIEW_USERNAME_KEY, adminUsername)
  }

  stripParams(params, ['edition', 'survey', 'adminPreview', 'adminUsername', 'embed', 'page'])
  const qs = params.toString()
  const next = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`
  window.history.replaceState(null, '', next)
}

/** Edition slug forced by admin preview for this tab/iframe (sessionStorage). */
export function getAdminPreviewEditionSlug(): string | null {
  try {
    if (sessionStorage.getItem(ADMIN_PREVIEW_ACTIVE_KEY) !== '1') return null
    return sessionStorage.getItem(ADMIN_PREVIEW_EDITION_KEY)?.trim() || null
  } catch {
    return null
  }
}

export function getAdminPreviewSurveyKey(): string | null {
  try {
    return sessionStorage.getItem(ADMIN_PREVIEW_SURVEY_KEY)
  } catch {
    return null
  }
}

export function getAdminPreviewUsername(): string | null {
  try {
    return sessionStorage.getItem(ADMIN_PREVIEW_USERNAME_KEY)
  } catch {
    return null
  }
}

export function isAdminPreviewSession(): boolean {
  try {
    return sessionStorage.getItem(ADMIN_PREVIEW_ACTIVE_KEY) === '1'
  } catch {
    return false
  }
}

export function isAdminEmbedSession(): boolean {
  try {
    return sessionStorage.getItem(ADMIN_PREVIEW_EMBED_KEY) === '1'
  } catch {
    return false
  }
}

export function getAdminPreviewPage(): string | null {
  try {
    return sessionStorage.getItem(ADMIN_PREVIEW_PAGE_KEY)
  } catch {
    return null
  }
}

// Run immediately on import
applyAdminPreviewQueryFromLocation()
